/**
 * Manus Markdown Parser
 * Parse báo cáo markdown từ Manus AI và extract thông tin định giá
 */

/**
 * Extract giá từ markdown text
 * Tìm pattern: "1.50 Tỷ VND đến 1.60 Tỷ VND" hoặc "1.54 Tỷ VND"
 */
const extractPriceRange = (text) => {
  if (!text) return null;

  // Pattern: "X Tỷ VND đến Y Tỷ VND" hoặc "X - Y Tỷ VND"
  const rangePattern = /(\d+\.?\d*)\s*Tỷ\s*VND\s*(?:đến|-)\s*(\d+\.?\d*)\s*Tỷ\s*VND/i;
  const rangeMatch = text.match(rangePattern);

  if (rangeMatch) {
    const minBillion = parseFloat(rangeMatch[1]);
    const maxBillion = parseFloat(rangeMatch[2]);
    return {
      min: minBillion * 1000000000,
      max: maxBillion * 1000000000,
      currency: "VND",
      formatted: `${rangeMatch[1]} - ${rangeMatch[2]} tỷ VND`,
    };
  }

  // Pattern: "X Tỷ VND" (single value)
  const singlePattern = /(\d+\.?\d*)\s*Tỷ\s*VND/i;
  const singleMatch = text.match(singlePattern);

  if (singleMatch) {
    const billion = parseFloat(singleMatch[1]);
    return {
      min: billion * 1000000000,
      max: billion * 1000000000,
      currency: "VND",
      formatted: `${singleMatch[1]} tỷ VND`,
    };
  }

  return null;
};

/**
 * Extract đơn giá trên m² từ markdown
 * Tìm pattern: "25.86 Triệu VND/m² đến 27.59 Triệu VND/m²"
 */
const extractPricePerM2 = (text) => {
  if (!text) return null;

  // Pattern: "X Triệu VND/m² đến Y Triệu VND/m²"
  const rangePattern =
    /(\d+\.?\d*)\s*Triệu\s*VND\/m²\s*(?:đến|-)\s*(\d+\.?\d*)\s*Triệu\s*VND\/m²/i;
  const rangeMatch = text.match(rangePattern);

  if (rangeMatch) {
    const minMillion = parseFloat(rangeMatch[1]);
    const maxMillion = parseFloat(rangeMatch[2]);
    return {
      min: minMillion * 1000000,
      max: maxMillion * 1000000,
      currency: "VND",
      formatted: `${rangeMatch[1]} - ${rangeMatch[2]} triệu/m²`,
    };
  }

  // Pattern: "X Triệu VND/m²" (single value)
  const singlePattern = /(\d+\.?\d*)\s*Triệu\s*VND\/m²/i;
  const singleMatch = text.match(singlePattern);

  if (singleMatch) {
    const million = parseFloat(singleMatch[1]);
    return {
      min: million * 1000000,
      max: million * 1000000,
      currency: "VND",
      formatted: `${singleMatch[1]} triệu/m²`,
    };
  }

  return null;
};

/**
 * Extract xu hướng thị trường từ section "### 3. Xu hướng Thị trường"
 */
const extractMarketTrend = (markdown) => {
  if (!markdown) return null;

  // Tìm section xu hướng thị trường
  const trendSection =
    /###\s*3\.\s*Xu hướng[^#]+?Xu hướng thị trường là\s*\*\*([^*]+)\*\*/i;
  const match = markdown.match(trendSection);

  if (match) {
    return match[1].trim();
  }

  // Fallback: tìm bất kỳ mention nào về xu hướng
  const fallbackPattern = /xu hướng.*?(?:là|:|đang)\s*\*\*([^*]+)\*\*/i;
  const fallbackMatch = markdown.match(fallbackPattern);

  if (fallbackMatch) {
    return fallbackMatch[1].trim();
  }

  return null;
};

/**
 * Extract summary từ markdown
 */
const extractSummary = (markdown) => {
  if (!markdown) return "";

  // Tìm section giá ước tính (thường chứa summary ngắn gọn nhất)
  const summaryPattern =
    /####\s*1\.\s*Giá ước tính[^>]+>(.*?)(?:\n\n|\*\*)/s;
  const match = markdown.match(summaryPattern);

  if (match) {
    return match[1].trim().replace(/\*/g, "");
  }

  // Fallback: lấy 200 ký tự đầu tiên của markdown
  const plainText = markdown.replace(/[#*`>[\]]/g, "").trim();
  return plainText.substring(0, 200) + (plainText.length > 200 ? "..." : "");
};

/**
 * Extract insights từ section "### 4. Các Yếu tố Ảnh hưởng đến Giá"
 */
const extractInsights = (markdown) => {
  if (!markdown) return [];

  const insights = [];

  // Tìm table các yếu tố ảnh hưởng
  const tableSection = /###\s*4\.\s*Các Yếu tố[^#]+?\|([^#]+?)(?:##|\n\n#)/s;
  const match = markdown.match(tableSection);

  if (match) {
    const tableContent = match[1];
    // Parse table rows (bỏ qua header)
    const rows = tableContent.split("\n").filter((row) => row.trim());

    rows.forEach((row) => {
      // Skip table separator
      if (row.includes(":---") || row.includes("Yếu tố")) return;

      const cells = row.split("|").map((cell) => cell.trim());
      if (cells.length >= 3 && cells[1] && cells[2]) {
        insights.push(`${cells[1]}: ${cells[2]} - ${cells[3] || ""}`);
      }
    });
  }

  // Nếu không tìm thấy table, tìm list items trong section đó
  if (insights.length === 0) {
    const listPattern = /###\s*4\.[^#]+?(?:\*\s*([^\n*]+)\n?)/g;
    let listMatch;
    while ((listMatch = listPattern.exec(markdown)) !== null) {
      insights.push(listMatch[1].trim());
    }
  }

  return insights.length > 0
    ? insights
    : ["Thông tin chi tiết trong báo cáo đính kèm"];
};

/**
 * Extract recommendations từ section "### 6. Khuyến nghị về Thời điểm Bán"
 */
const extractRecommendations = (markdown) => {
  if (!markdown) return [];

  const recommendations = [];

  // Tìm section khuyến nghị
  const recSection = /###\s*6\.\s*Khuyến nghị[^#]+?\|([^#]+?)(?:\*\*\*|##)/s;
  const match = markdown.match(recSection);

  if (match) {
    const tableContent = match[1];
    const rows = tableContent.split("\n").filter((row) => row.trim());

    rows.forEach((row) => {
      // Skip table separator and header
      if (row.includes(":---") || row.includes("Kịch bản")) return;

      const cells = row.split("|").map((cell) => cell.trim());
      if (cells.length >= 3 && cells[1] && cells[2]) {
        recommendations.push(`${cells[1]}: ${cells[2]} - ${cells[3] || ""}`);
      }
    });
  }

  return recommendations.length > 0
    ? recommendations
    : ["Xem chi tiết khuyến nghị trong báo cáo"];
};

/**
 * Extract độ tin cậy (confidence) - thường không có trong markdown Manus
 * Return default value
 */
const extractConfidence = (markdown) => {
  if (!markdown) return "Cao";

  // Tìm pattern độ tin cậy nếu có
  const confidencePattern = /độ tin cậy[:\s]*([^\n.]+)/i;
  const match = markdown.match(confidencePattern);

  if (match) {
    return match[1].trim();
  }

  return "Cao"; // Default
};

/**
 * Main function: Parse markdown báo cáo từ Manus
 * @param {string} markdown - Nội dung markdown từ Manus
 * @returns {object} - Structured data object
 */
export const parseManusMarkdownReport = (markdown) => {
  if (!markdown) {
    return {
      success: false,
      error: "Markdown content is empty",
      data: null,
    };
  }

  try {
    // Extract price estimate
    const priceEstimate = extractPriceRange(markdown);

    // Extract price per m²
    const pricePerM2 = extractPricePerM2(markdown);

    // Extract market trend
    const marketTrend = extractMarketTrend(markdown);

    // Extract summary
    const summary = extractSummary(markdown);

    // Extract insights
    const insights = extractInsights(markdown);

    // Extract recommendations
    const recommendations = extractRecommendations(markdown);

    // Extract confidence
    const confidence = extractConfidence(markdown);

    const parsedData = {
      price_estimate: priceEstimate,
      price_per_m2: pricePerM2,
      market_trend: marketTrend,
      confidence,
      insights,
      recommendations,
      summary,
    };

    return {
      success: true,
      data: parsedData,
      rawMarkdown: markdown,
    };
  } catch (error) {
    console.error("Error parsing Manus markdown:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      rawMarkdown: markdown,
    };
  }
};

/**
 * Helper: Download file từ URL
 */
export const downloadFileContent = async (fileUrl) => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`);
    }
    const content = await response.text();
    return {
      success: true,
      content,
    };
  } catch (error) {
    console.error("Error downloading file:", error);
    return {
      success: false,
      error: error.message,
      content: null,
    };
  }
};

/**
 * Tạo file markdown object từ text content (fallback khi Manus không tạo file)
 * @param {string} markdownText - Nội dung markdown từ Manus output
 * @param {string} filename - Tên file (optional)
 * @returns {object} - File object với URL có thể download
 */
export const createMarkdownFileFromText = (markdownText, filename = null) => {
  if (!markdownText) {
    return {
      success: false,
      error: "Markdown text is empty",
      file: null,
      fileUrl: null,
    };
  }

  try {
    // Tạo filename nếu không có
    const finalFilename =
      filename || `bao_cao_dinh_gia_${Date.now()}.md`;

    // Tạo Blob từ markdown text
    const blob = new Blob([markdownText], { type: "text/markdown" });

    // Tạo URL từ Blob để có thể download
    const fileUrl = URL.createObjectURL(blob);

    // Tạo File object
    const file = new File([blob], finalFilename, { type: "text/markdown" });

    return {
      success: true,
      file,
      fileUrl,
      fileName: finalFilename,
      mimeType: "text/markdown",
      size: blob.size,
    };
  } catch (error) {
    console.error("Error creating markdown file:", error);
    return {
      success: false,
      error: error.message,
      file: null,
      fileUrl: null,
    };
  }
};

/**
 * Download markdown file to user's computer
 * @param {string} markdownText - Nội dung markdown
 * @param {string} filename - Tên file
 */
export const downloadMarkdownFile = (markdownText, filename = null) => {
  const result = createMarkdownFileFromText(markdownText, filename);
  
  if (!result.success) {
    console.error("Failed to create markdown file:", result.error);
    return false;
  }

  try {
    // Tạo link tạm để trigger download
    const link = document.createElement("a");
    link.href = result.fileUrl;
    link.download = result.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup URL sau 1 giây
    setTimeout(() => {
      URL.revokeObjectURL(result.fileUrl);
    }, 1000);

    return true;
  } catch (error) {
    console.error("Error downloading markdown file:", error);
    return false;
  }
};

const manusMarkdownParser = {
  parseManusMarkdownReport,
  downloadFileContent,
  createMarkdownFileFromText,
  downloadMarkdownFile,
  extractPriceRange,
  extractPricePerM2,
  extractMarketTrend,
  extractInsights,
  extractRecommendations,
};

export default manusMarkdownParser;

